// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Shared "email this invoice/quote" transport.
 *
 * Extracted from `share-document-modal.tsx` so BOTH entry points ride the exact
 * same path:
 *   • the Share modal (send to one or more chosen recipients), and
 *   • the "Send to myself" quick action (send to the signed-in user's own inbox).
 *
 * PORTAL EDIT (2026-09-17): NO PDF IS BUILT OR POSTED HERE. crm-api's
 * `Api::V1::InvoicesController#share` renders the attachment itself with
 * `InvoicePdfRenderer` — the one generator in the stack — and ignores any posted
 * `pdf`, so the file a recipient gets is the file the portal previews. This
 * only names the recipients, subject and note. See lib/documentPdf.ts.
 *
 * NOTE: crm-api does not grant `#share` to reseller actors (it is absent from
 * `allow_reseller_actor` on InvoicesController), so from this portal the call
 * is refused with 403 until crm-api opens it.
 */
import api from "@crm-src/lib/api"

/** A share target. Only `email` is required; `name` (when present) shows in the
 *  greeting. crm-api reads `recipient["email"]`/`["name"]` off each row. Extra
 *  fields (id/type/company on the modal's `EmailRecipient`) are harmless. */
export interface ShareRecipient {
  email: string
  name?: string
}

export interface SendSharedDocumentOptions {
  documentId: string
  documentType: "invoice" | "quote"
  recipients: ReadonlyArray<ShareRecipient>
  subject: string
  body: string
}

export interface SendSharedDocumentResult {
  success?: boolean
  message?: string
  sent_count?: number
  failed_count?: number
  errors?: string[]
  error?: string
}

/**
 * Email the document to `recipients` via crm-api's share endpoint, which
 * attaches its own rendering of it. Resolves with the parsed API body (check
 * `.success`); rejects the same way axios would on a network/HTTP error so
 * callers can surface the server's reason.
 */
export async function sendSharedDocument(
  opts: SendSharedDocumentOptions,
): Promise<SendSharedDocumentResult> {
  const { documentId, recipients, subject, body } = opts

  const formData = new FormData()
  formData.append("recipients", JSON.stringify(recipients))
  formData.append("subject", subject)
  formData.append("body", body)

  const response = await api.post(`/invoices/${documentId}/share`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data as SendSharedDocumentResult
}
